/**
 * Utility functions shared across polyline extraction modules
 */
import { Vector3Like } from 'three'

// === Core math utilities (used by multiple modules) ======================
export const distSq = (a: Vector3Like, b: Vector3Like) => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const dz = a.z - b.z
  return dx * dx + dy * dy + dz * dz
}

export const dist = (a: Vector3Like, b: Vector3Like) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)

export const sub = (a: Vector3Like, b: Vector3Like): Vector3Like => ({
  x: a.x - b.x,
  y: a.y - b.y,
  z: a.z - b.z,
})

export const dot = (a: Vector3Like, b: Vector3Like) => a.x * b.x + a.y * b.y + a.z * b.z

export const magnitude = (v: Vector3Like): number => Math.hypot(v.x, v.y, v.z)

export const normalize = (v: Vector3Like): Vector3Like => {
  const len = Math.hypot(v.x, v.y, v.z)
  return len > 0 ? { x: v.x / len, y: v.y / len, z: v.z / len } : { x: 0, y: 0, z: 1 }
}

// === Array conversion utilities ===========================================

/**
 * Convert interleaved Float32Array [x,y,z,...] to Vector3Like[]
 */
export const interleavedToVec3 = (arr: Float32Array): Vector3Like[] => {
  const out: Vector3Like[] = []
  for (let i = 0; i < arr.length; i += 3) {
    out.push({
      x: arr[i],
      y: arr[i + 1],
      z: arr[i + 2],
    })
  }
  return out
}

// === Geometric utilities (used by multiple modules) ======================

/**
 * Compute centroid of points
 */
export const computeCentroid = (pts: Vector3Like[]) => {
  const c = { x: 0, y: 0, z: 0 }
  if (pts.length === 0) return c
  pts.forEach((p) => {
    c.x += p.x
    c.y += p.y
    c.z += p.z
  })
  c.x /= pts.length
  c.y /= pts.length
  c.z /= pts.length
  return c
}

/**
 * Compute polyline length (sum of segment distances)
 */
export const polylineLength = (pl: Vector3Like[]): number => {
  if (pl.length < 2) return 0
  let length = 0
  for (let i = 1; i < pl.length; i += 1) {
    length += dist(pl[i - 1], pl[i])
  }
  return length
}

// === Spatial hash utilities ==============================================

export interface SpatialHash {
  map: Map<string, number[]>
  keyOf: (p: Vector3Like) => string
}

/**
 * Build spatial hash for fast neighbor lookups
 */
export const buildSpatialHash = (pts: Vector3Like[], cellSize: number): SpatialHash => {
  const map = new Map<string, number[]>()
  const keyOf = (p: Vector3Like) =>
    `${Math.floor(p.x / cellSize)}_${Math.floor(p.y / cellSize)}_${Math.floor(p.z / cellSize)}`

  pts.forEach((p, i) => {
    const k = keyOf(p)
    const arr = map.get(k)
    if (arr) arr.push(i)
    else map.set(k, [i])
  })

  return { map, keyOf }
}

/**
 * Get all neighboring spatial hash bucket keys (27 neighbors in 3D)
 */
export const neighborBucketKeys = (kx: number, ky: number, kz: number): string[] => {
  const keys: string[] = []
  for (let dx = -1; dx <= 1; dx += 1) {
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dz = -1; dz <= 1; dz += 1) {
        keys.push(`${kx + dx}_${ky + dy}_${kz + dz}`)
      }
    }
  }
  return keys
}

/**
 * Compute principal direction (unit vector) of points via covariance power iteration
 */
export const computePrincipalDirection = (pts: Vector3Like[], indices?: number[], maxIter = 20) => {
  const n = indices ? indices.length : pts.length
  if (n === 0) return { x: 1, y: 0, z: 0 }

  // compute centroid
  let cx = 0
  let cy = 0
  let cz = 0
  if (indices) {
    indices.forEach((i) => {
      cx += pts[i].x
      cy += pts[i].y
      cz += pts[i].z
    })
  } else {
    pts.forEach((p) => {
      cx += p.x
      cy += p.y
      cz += p.z
    })
  }
  cx /= n
  cy /= n
  cz /= n

  // build covariance entries
  let cxx = 0
  let cxy = 0
  let cxz = 0
  let cyy = 0
  let cyz = 0
  let czz = 0
  if (indices) {
    indices.forEach((i) => {
      const p = pts[i]
      const x = p.x - cx
      const y = p.y - cy
      const z = p.z - cz
      cxx += x * x
      cxy += x * y
      cxz += x * z
      cyy += y * y
      cyz += y * z
      czz += z * z
    })
  } else {
    pts.forEach((p) => {
      const x = p.x - cx
      const y = p.y - cy
      const z = p.z - cz
      cxx += x * x
      cxy += x * y
      cxz += x * z
      cyy += y * y
      cyz += y * z
      czz += z * z
    })
  }

  // power iteration for dominant eigenvector of covariance matrix
  let vx = 1
  let vy = 0
  let vz = 0
  for (let it = 0; it < maxIter; it += 1) {
    const wx = cxx * vx + cxy * vy + cxz * vz
    const wy = cxy * vx + cyy * vy + cyz * vz
    const wz = cxz * vx + cyz * vy + czz * vz
    const len = Math.hypot(wx, wy, wz) || 1
    vx = wx / len
    vy = wy / len
    vz = wz / len
  }
  return { x: vx, y: vy, z: vz }
}
